import { Property, PropertyType } from '@/api/types/categories.types';
import {
  GroupedProperties,
  UpdateUrlParamsProps,
} from '@/components/Filters/FiltersDrawer.types';
import { isBooleanString } from '@/utils/helpers';
import { QUERY } from '@/constants/common';
import { PHONE_NUMBER_PROPERTY_LEGACY_ID } from '@/features/PhoneNumberSearch';

const numericKeyPattern = /^p\[\d+]$/;
const arrayKeyPattern = /^(p|q)\[[^\]]+]\[]$/;
const phoneNumberKey = `p[${PHONE_NUMBER_PROPERTY_LEGACY_ID}]`;

function isRestrictedParam(key: string) {
  return [
    `${QUERY.q}[${QUERY.price}][]`,
    `${QUERY.q}[${QUERY.regionId}]`,
  ].includes(key);
}

function stripEmptySearchParams(searchParams: URLSearchParams) {
  const uniqueKeys = [...new Set(searchParams.keys())];
  uniqueKeys.forEach(key => {
    const values = searchParams.getAll(key);

    if (values.length > 0 && values.every(value => value === '')) {
      searchParams.delete(key);
    }
  });
}

function removePhoneNumberQueryParam(searchParams: URLSearchParams) {
  searchParams.delete(phoneNumberKey);
}

function modifyUrlPath(urlObj: URL, hasCategory: boolean) {
  try {
    const previousPathname = urlObj.pathname;

    if (!hasCategory) {
      // eslint-disable-next-line no-param-reassign
      urlObj.pathname = previousPathname.replace(
        /^\/elanlar(\/.*)?$/,
        '/elanlar'
      );
    }

    if (urlObj.pathname !== previousPathname) {
      removePhoneNumberQueryParam(urlObj.searchParams);
    }

    return urlObj;
  } catch (error) {
    return urlObj;
  }
}

export function updateURLParameters({
  url,
  params,
  booleanCollection,
  rangeCollection,
  collection,
  hasCategory = false,
}: UpdateUrlParamsProps): string {
  const updatedURL = new URL(url);
  const searchParams = new URLSearchParams(updatedURL.search);

  [QUERY.button, QUERY.mobileSite].forEach(param => searchParams.delete(param));

  for (const key in params) {
    if (params[key] !== undefined) {
      if (Array.isArray(params[key])) {
        searchParams.delete(key);
        (params[key] as Array<string | null | undefined>).forEach(value =>
          searchParams.append(key, value == null ? '' : String(value))
        );
      } else {
        const value = params[key];
        searchParams.set(key, value == null ? '' : String(value));
      }
    }
  }

  if (collection) {
    for (const key of [...searchParams.keys()]) {
      const value = searchParams.get(key);

      if (isRestrictedParam(key) || key === phoneNumberKey) continue;

      if (numericKeyPattern.test(key) && Number(value)) {
        searchParams.delete(key);
      }
    }

    if (Object.entries(collection).length > 0) {
      for (const [key, value] of Object.entries(collection)) {
        if (value !== undefined) {
          searchParams.set(`p[${key}]`, value);
        } else {
          searchParams.delete(`p[${key}]`);
        }
      }
    }
  }

  if (booleanCollection) {
    for (const key of [...searchParams.keys()]) {
      const value = searchParams.get(key);

      if (isRestrictedParam(key)) continue;

      if (numericKeyPattern.test(key) && isBooleanString(value)) {
        searchParams.delete(key);
      }
    }

    if (Object.entries(booleanCollection).length > 0) {
      for (const [key, value] of Object.entries(booleanCollection)) {
        if (value !== undefined) {
          searchParams.set(`p[${key}]`, String(value));
        } else {
          searchParams.delete(`p[${key}]`);
        }
      }
    }
  }

  if (rangeCollection) {
    for (const key of [...searchParams.keys()]) {
      if (isRestrictedParam(key)) continue;

      if (arrayKeyPattern.test(key)) {
        searchParams.delete(key);
      }
    }

    if (Object.entries(rangeCollection).length > 0) {
      for (const [key, values] of Object.entries(rangeCollection)) {
        const queryKey = `p[${key}][]`;
        searchParams.delete(queryKey);

        if (Array.isArray(values)) {
          values.forEach(value =>
            searchParams.append(queryKey, value == null ? '' : String(value))
          );
        }
      }
    }
  }

  stripEmptySearchParams(searchParams);
  updatedURL.search = searchParams.toString();
  return modifyUrlPath(updatedURL, hasCategory).toString();
}

export function replaceUrlPath(baseUrl: string, newPath: string) {
  const url = new URL(baseUrl);
  const previousPathname = url.pathname;
  url.pathname = newPath.startsWith('/') ? newPath : `/${newPath}`;

  if (url.pathname !== previousPathname) {
    removePhoneNumberQueryParam(url.searchParams);
  }

  return url.toString();
}

export function separateBooleanProperties(properties?: Property[]) {
  const initialGroupedProperties = {
    booleanProperties: new Set<Property>(),
    restProperties: new Set<Property>(),
  };

  const groupedProperties =
    properties?.reduce<GroupedProperties>((acc, p) => {
      switch (p.type) {
        case PropertyType.BooleanProperty:
          acc.booleanProperties.add(p);
          break;
        case PropertyType.CollectionProperty:
        case PropertyType.RangeProperty:
          acc.restProperties.add(p);
          break;
        default:
          break;
      }
      return acc;
    }, initialGroupedProperties) ?? initialGroupedProperties;

  return {
    booleanProperties: Array.from(groupedProperties.booleanProperties),
    restProperties: Array.from(groupedProperties.restProperties),
  };
}

export function hrefWithSearchParams(
  clearUrl: string,
  searchParams: URLSearchParams
) {
  const query = searchParams.toString();
  return query ? `${clearUrl}?${query}` : clearUrl;
}
